import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { CartFab, CartDrawer } from '../components/Cart';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const QRMenu = ({ restaurantIdOverride, slugOverride, tableNumberOverride, prefetchedMenu }) => {
  const params = useParams();
  const restaurantId = params.restaurantId || restaurantIdOverride;
  const tableNumber = params.tableNumber || tableNumberOverride;
  const navigate = useNavigate();
  const { t, lang, setLang, getLocalized } = useLanguage();
  const [data, setData] = useState(prefetchedMenu || null);
  const [loading, setLoading] = useState(!prefetchedMenu);
  const [error, setError] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // When accessed via slug URL (e.g. /baton-cafe), preserve slug in category/item links
  const menuRoot = slugOverride ? `/${slugOverride}` : `/qr-menu/${restaurantId}`;
  const tableSuffix = slugOverride ? `/masa${tableNumber}` : `/t/${tableNumber}`;
  const basePath = tableNumber ? `${menuRoot}${tableSuffix}` : menuRoot;

  useEffect(() => {
    // Skip refetch when we already have prefetched data AND the language hasn't changed.
    // If lang changes (or no prefetched data), fetch fresh menu.
    if (prefetchedMenu && !data) {
      setData(prefetchedMenu);
      setLoading(false);
      return;
    }
    fetchMenu();
  }, [restaurantId, lang]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      doSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, lang]);

  const fetchMenu = async () => {
    try {
      const { data: menuData } = await axios.get(`${BACKEND_URL}/api/menu/qr/${restaurantId}?lang=${lang}`);
      setData(menuData);
    } catch (err) {
      setError('Menyu yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const doSearch = async () => {
    setSearching(true);
    try {
      const { data: result } = await axios.get(
        `${BACKEND_URL}/api/menu/qr/${restaurantId}/search?q=${encodeURIComponent(searchQuery)}`
      );
      setSearchResults(result.items);
    } catch (err) {
      console.error('Search error', err);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <p className="text-red-600 text-xl">{error || 'Restoran tapılmadı'}</p>
        </div>
      </div>
    );
  }

  // INACTIVE / PASSIVE RESTAURANT — show block screen
  if (data.inactive || data.restaurant?.is_active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 p-4" data-testid="qr-menu-inactive">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-amber-100 flex items-center justify-center">
            <span className="text-5xl">⏸️</span>
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-3">
            {data.restaurant?.name || 'Restoran'}
          </h1>
          <div className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-bold uppercase rounded-full mb-4">
            ● Xidmət müvəqqəti dayandırılıb
          </div>
          <p className="text-stone-600 text-sm leading-relaxed mb-6">
            Bu restoranın QR menyu xidməti hal-hazırda <strong>aktiv deyil</strong>.
            Restoran sahibi və ya administrator ilə əlaqə saxlayın.
          </p>
          <div className="border-t border-stone-200 pt-4">
            <p className="text-xs text-stone-400">SmartMenyu.az • Azərbaycanın #1 QR Menyu Platforması 🇦🇿</p>
          </div>
        </div>
      </div>
    );
  }

  const { restaurant, categories } = data;
  const currencySymbol = restaurant.currency_symbol || '₼';

  const langOptions = [
    { code: 'az', label: 'Azərbaycan', flag: 'AZ' },
    { code: 'tr', label: 'Türkçe', flag: 'TR' },
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'ru', label: 'Русский', flag: 'RU' },
  ];

  const currentLang = langOptions.find((l) => l.code === lang);

  return (
    <div className="min-h-screen bg-[#2B170D]" data-testid="qr-menu-page">
      {/* HEADER with restaurant cover background */}
      <div
        className="relative h-32 md:h-40 bg-cover bg-center"
        style={{
          backgroundImage: restaurant.cover_url
            ? `url(${restaurant.cover_url})`
            : 'linear-gradient(135deg, #1a0e08 0%, #3d2418 50%, #1a0e08 100%)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {restaurant.logo_url ? (
              <img
                src={restaurant.logo_url}
                alt={restaurant.name}
                className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-white object-cover"
              />
            ) : (
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-emerald-600 flex items-center justify-center text-white text-2xl font-bold border-2 border-white">
                {restaurant.name?.[0]}
              </div>
            )}
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white tracking-wide" data-testid="restaurant-name">
                {restaurant.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {tableNumber && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg" data-testid="table-badge">
                    🪑 Masa #{tableNumber}
                  </span>
                )}
                {restaurant.service_fee_percentage > 0 && (
                  <span className="text-xs md:text-sm text-amber-200" data-testid="service-fee">
                    {t('service_fee').toUpperCase()}: {restaurant.service_fee_percentage}%
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Search button */}
            <button
              onClick={() => setShowSearch(true)}
              className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition"
              data-testid="search-button"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>

            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full px-3 py-2 transition"
                data-testid="lang-switcher"
              >
                <span className="text-white text-sm font-bold">{currentLang?.flag}</span>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showLangMenu && (
                <div className="absolute right-0 top-12 bg-white rounded-xl shadow-2xl py-2 min-w-[160px] z-50">
                  {langOptions.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => {
                        setLang(opt.code);
                        setShowLangMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition flex items-center space-x-3 ${
                        lang === opt.code ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'
                      }`}
                      data-testid={`lang-option-${opt.code}`}
                    >
                      <span className="text-sm font-bold w-8">{opt.flag}</span>
                      <span className="text-sm">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CATEGORY SLIDER (horizontal scroll) */}
      <div className="bg-[#2B170D] border-b border-white/10 shadow-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center space-x-3 overflow-x-auto scrollbar-hide" data-testid="category-slider">
            <button
              onClick={() => setShowSideMenu(true)}
              className="flex items-center space-x-2 bg-[#5C3A24] hover:bg-[#6E4730] rounded-full px-4 py-2 shadow-sm flex-shrink-0 transition-colors"
              data-testid="sidemenu-toggle"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="text-sm font-medium text-white">{t('menu')}</span>
            </button>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`${basePath}/category/${cat.id}`}
                className="flex items-center space-x-2 bg-[#4A2C1A] hover:bg-[#5C3A24] rounded-full pl-1 pr-4 py-1 transition-colors flex-shrink-0 group"
                data-testid={`category-tab-${cat.id}`}
              >
                {cat.image_url ? (
                  <img
                    src={cat.image_url}
                    alt={getLocalized(cat, 'name')}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-700 text-sm font-bold">
                    {getLocalized(cat, 'name')?.[0]}
                  </div>
                )}
                <span className="text-sm font-medium text-white whitespace-nowrap">
                  {getLocalized(cat, 'name')}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white inline-block relative" data-testid="our-menu-heading">
            {t('our_menu')}
            <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-emerald-500 rounded"></span>
          </h2>
        </div>

        {categories.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">Menyu hələ hazırlanmayıb</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`${basePath}/category/${cat.id}`}
                className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                data-testid={`category-card-${cat.id}`}
                style={{ minHeight: '260px' }}
              >
                {/* Background image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{
                    backgroundImage: cat.image_url
                      ? `url(${cat.image_url})`
                      : 'linear-gradient(135deg, #4a2c1a 0%, #6b3e25 50%, #4a2c1a 100%)',
                  }}
                ></div>
                {/* Dark overlay for readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50"></div>

                {/* Item count badge */}
                <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full" data-testid={`category-count-${cat.id}`}>
                  {cat.item_count || 0} {t('products')}
                </div>

                {/* Bottom gradient bar with name */}
                <div className="absolute bottom-0 left-0 right-0 px-4 py-3">
                  <div className="bg-gradient-to-r from-amber-700 via-amber-600 to-amber-700 rounded-full py-2 px-4 shadow-lg">
                    <p className="text-white font-bold text-center tracking-wider text-sm uppercase">
                      {getLocalized(cat, 'name')}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="bg-black/30 border-t border-white/10 text-stone-300 mt-12 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="font-bold text-lg text-white mb-2">{restaurant.name}</p>
          <p className="text-sm">📍 {restaurant.address}</p>
          <p className="text-sm">📞 {restaurant.phone}</p>
          <p className="text-xs mt-4 text-stone-500">Powered by Restoran İdarəetmə Sistemi</p>
        </div>
      </div>

      {/* SIDE MENU DRAWER */}
      {showSideMenu && (
        <div className="fixed inset-0 z-[60]" data-testid="side-menu-drawer">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSideMenu(false)}
          ></div>
          {/* Drawer panel */}
          <div className="absolute left-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto animate-slide-in-left">
            {/* Drawer header */}
            <div
              className="relative px-5 py-6 bg-cover bg-center"
              style={{
                backgroundImage: restaurant.cover_url
                  ? `url(${restaurant.cover_url})`
                  : 'linear-gradient(135deg, #1a0e08 0%, #3d2418 50%, #1a0e08 100%)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/80"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {restaurant.logo_url ? (
                    <img src={restaurant.logo_url} alt={restaurant.name} className="w-12 h-12 rounded-full border-2 border-white object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl font-bold border-2 border-white">
                      {restaurant.name?.[0]}
                    </div>
                  )}
                  <div>
                    <h3 className="text-white font-bold text-lg">{restaurant.name}</h3>
                    {restaurant.service_fee_percentage > 0 && (
                      <p className="text-xs text-amber-200">{t('service_fee')}: {restaurant.service_fee_percentage}%</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowSideMenu(false)}
                  className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                  data-testid="side-menu-close"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Categories list */}
            <div className="p-4">
              <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-3 px-2">
                {t('menu')}
              </h4>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`${basePath}/category/${cat.id}`}
                    onClick={() => setShowSideMenu(false)}
                    className="flex items-center space-x-3 p-2.5 rounded-xl hover:bg-emerald-50 transition group"
                    data-testid={`side-menu-cat-${cat.id}`}
                  >
                    {cat.image_url ? (
                      <img src={cat.image_url} alt={getLocalized(cat, 'name')} className="w-12 h-12 rounded-xl object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-700 font-bold text-lg">
                        {getLocalized(cat, 'name')?.[0]}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 group-hover:text-emerald-700 transition">
                        {getLocalized(cat, 'name')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {cat.item_count || 0} {t('products')}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                ))}
              </div>

              {/* Contact info */}
              <div className="mt-6 pt-6 border-t border-gray-200 px-2 text-sm text-gray-600 space-y-1">
                <p>📍 {restaurant.address}</p>
                <p>📞 {restaurant.phone}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEARCH MODAL */}
      {showSearch && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-20 px-4" onClick={() => setShowSearch(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-stone-200">
              <div className="flex items-center space-x-3 bg-stone-100 rounded-full px-4 py-3">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search_placeholder')}
                  className="flex-1 bg-transparent outline-none text-gray-800"
                  data-testid="search-input"
                />
                <button onClick={() => setShowSearch(false)} className="text-gray-500 hover:text-gray-700">
                  ✕
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[60vh]">
              {searching && (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                </div>
              )}
              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="p-8 text-center text-gray-500">{t('no_results')}</p>
              )}
              {!searching &&
                searchResults.map((item) => (
                  <Link
                    key={item.id}
                    to={`${basePath}/item/${item.id}`}
                    onClick={() => setShowSearch(false)}
                    className="flex items-center space-x-4 p-4 hover:bg-stone-50 transition border-b border-stone-100"
                    data-testid={`search-result-${item.id}`}
                  >
                    {item.image_url ? (
                      <img src={item.image_url} alt={getLocalized(item, 'name')} className="w-16 h-16 rounded-lg object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-stone-200 flex items-center justify-center text-stone-500 text-2xl">🍽️</div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{getLocalized(item, 'name')}</h4>
                      {getLocalized(item, 'description') && (
                        <p className="text-sm text-gray-500 line-clamp-1">{getLocalized(item, 'description')}</p>
                      )}
                    </div>
                    <span className="font-bold text-emerald-600">{item.price} {currencySymbol}</span>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      )}

      <CartFab lang={lang} />
      <CartDrawer restaurant={restaurant} lang={lang} />
    </div>
  );
};
